import { Box, drawerClasses } from "@mui/material";
import { IAddNotePayload, INote, INotePayload, NoteClickSignature, NoteDrawerMode } from "models/notes";
import useReadNote from "hooks/notes/useReadNote";
import { useEffect, useState, useRef } from "react";
import DeletedNoteContent from "./DeletedNoteContent";
import NoteButtonGroup from "./NoteButtonGroup";
import NoteHeading from "./NoteHeading";
import NoteIndicatorLine from "./NoteIndicatorLine";
import NoteMarkdown from "./NoteMarkdown";
import NoteQuoteCard from "./NoteQuoteCard";
import NoteEdit from "./NoteEdit";

type NoteContentProps = {
    note: INote;
    allowReply?: boolean;
    isChild?: boolean;
    onReplyClick: NoteClickSignature;
    onEditClick: NoteClickSignature;
    onDeleteClick: NoteClickSignature;
    mode: NoteDrawerMode;
    open: boolean;
    sourceNote?: INote | null;
    draft?: boolean;
    onClose: () => void;
    onSave: (mode: NoteDrawerMode, payload: INotePayload | IAddNotePayload, noteId?: number) => void;
};

const useIntersectionObserver = (ref: any, rootMargin = '0px') => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            setIsVisible(entry.isIntersecting);
        },
            {
                rootMargin
            }
        );

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => {
            observer.disconnect();
        };
    }, [ref, rootMargin]);

    return isVisible;
};

export const NoteChildren: React.FC<NoteContentProps> = (props) => {
    const {
        note,
        onReplyClick,
        onEditClick,
        onDeleteClick,
        mode,
        open,
        sourceNote,
        draft,
        onClose,
        onSave
    } = props;

    return (
        <>
            {note.children?.map((childNote, index) => {
                const showTopBorder = index === 0
                    ? note.isFocused === false
                    : note.children && !note.children[index - 1].isFocused;

                return (
                    <Box key={index} sx={{
                        position: 'relative',
                        marginTop: showTopBorder ? '-1px' : undefined,
                        borderTop: (theme) => showTopBorder ? `1px solid ${theme.palette.divider}` : 'none',
                    }}>
                        <NoteIndicatorLine hasFocus={childNote.isFocused ?? false} />
                        <Box key={index} sx={{
                            marginLeft: '60px',
                            paddingTop: '16px',
                            paddingBottom: '16px'
                        }}>
                            <NoteHeading note={childNote} avatar />
                            {open && mode === 'edit' && sourceNote && sourceNote.id === childNote.id &&
                                <NoteEdit
                                    mode={mode}
                                    sourceNote={sourceNote}
                                    onClose={onClose}
                                    onSave={onSave}
                                    draft={draft}
                                />
                                ||
                                <>
                                    <NoteContent note={childNote} isChild allowReply
                                        onReplyClick={onReplyClick}
                                        onEditClick={onEditClick}
                                        onDeleteClick={onDeleteClick}
                                        mode={mode}
                                        open={open}
                                        sourceNote={sourceNote}
                                        onClose={onClose}
                                        onSave={onSave}
                                        draft={draft}
                                    />
                                    {open && mode === 'reply' && sourceNote && sourceNote.id === childNote.id &&
                                        <NoteEdit
                                            mode={mode}
                                            sourceNote={sourceNote}
                                            onClose={onClose}
                                            onSave={onSave}
                                        />
                                    }
                                </>
                            }
                        </Box>
                    </Box>
                );
            })}
        </>
    );
};

const NoteContent: React.FC<NoteContentProps> = (props) => {
    const {
        note,
        onReplyClick,
        onEditClick,
        onDeleteClick,
        mode,
        open,
        sourceNote,
        draft,
        onClose,
        onSave
    } = props;
    const isChild = props.isChild ?? false;
    const allowReply = props.allowReply ?? false;

    const handleRead = useReadNote();
    const [readHandled, setReadHandled] = useState(false);
    const noteRef = useRef<HTMLDivElement>(null);
    const isVisible = useIntersectionObserver(noteRef);

    useEffect(() => {
        if (!(note.isRead ?? false) && isVisible) {
            handleRead(note);
            note.isRead = true;
        }
    }, [isVisible]);

    if (note.isDeleted) {
        return (<>
            <Box ref={noteRef} sx={{
                position: 'relative'
            }}>
                {isChild || note.children?.length === 0
                    ? null
                    : <NoteIndicatorLine hasFocus={note.isFocused ?? false} />
                }
                <DeletedNoteContent />
            </Box>
            <NoteChildren
                note={note}
                onReplyClick={onReplyClick}
                onEditClick={onEditClick}
                onDeleteClick={onDeleteClick}
                mode={mode}
                open={open}
                sourceNote={sourceNote}
                onClose={onClose}
                onSave={onSave}
            />
        </>);
    }

    return (
        <>
            <Box ref={noteRef} sx={{
                position: 'relative'
            }}>
                {isChild ? <></> : <NoteIndicatorLine hasFocus={note.isFocused ?? false} />}
                <Box sx={{
                    borderRadius: '4px',
                    backgroundColor: (theme) => note.isFocused
                        ? theme.palette.primary.light
                        : isChild ? 'transparent' : theme.palette.grey[100],

                }}>
                    {note.quotedNote ? <NoteQuoteCard note={note.quotedNote} /> : null}
                    <NoteMarkdown note={note.note} isChild={isChild} />
                    <NoteButtonGroup note={note} allowReply={allowReply}
                        onReplyClick={onReplyClick}
                        onEditClick={onEditClick}
                        onDeleteClick={onDeleteClick}
                    />
                </Box>
            </Box>
            <NoteChildren
                note={note}
                onReplyClick={onReplyClick}
                onEditClick={onEditClick}
                onDeleteClick={onDeleteClick}
                mode={mode}
                open={open}
                sourceNote={sourceNote}
                onClose={onClose}
                onSave={onSave}
                draft={draft}
            />
        </>
    );
};

export default NoteContent;